import { writable, get } from 'svelte/store';
import { apiClient } from '$lib/api/client';

/** One certification a search can filter on. */
export interface Certification {
  slug: string;
  label: string;
  /** What it is, and that the company states it. */
  means?: string;
}

/** One group of them: any inside a group, all of the groups. */
export interface CertificationGroup {
  key: string;
  label: string;
  certifications: Certification[];
}

export interface CertificationCatalogue {
  groups: CertificationGroup[];
}

export const certificationCatalogue = writable<CertificationCatalogue | null>(null);
export const certificationCatalogueFailed = writable<boolean>(false);

let inFlight: Promise<void> | null = null;

/**
 * Fetches the vocabulary once per page load, from the server that runs the
 * query, for the reason the technology catalogue does: a copy here would drift,
 * and a slug the server does not know is dropped silently.
 */
export async function loadCertificationCatalogue(): Promise<void> {
  if (get(certificationCatalogue) !== null) return;
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const response = await apiClient.get<CertificationCatalogue>('/filters/certifications');
      certificationCatalogue.set(response.data);
      certificationCatalogueFailed.set(false);
    } catch {
      certificationCatalogueFailed.set(true);
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

// In development, forget the catalogue on a hot reload, as the technology
// store does: otherwise an edited vocabulary does not show until a full reload.
if (import.meta.hot) {
  import.meta.hot.on('vite:beforeUpdate', () => {
    certificationCatalogue.set(null);
    certificationCatalogueFailed.set(false);
  });
}
